package info.rezident.app.seo

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document
import org.w3c.dom.*

private fun Document.ensureMetaTag(attributeName: String, attributeValue: String): HTMLMetaElement {
    val head: Element = head ?: error("Document head is not available")
    val selector: String = "meta[$attributeName='$attributeValue']"
    val existingElement: HTMLMetaElement? = head.querySelector(selector) as? HTMLMetaElement
    if (existingElement != null) {
        return existingElement
    }

    val newElement: HTMLMetaElement = createElement("meta") as HTMLMetaElement
    newElement.setAttribute(attributeName, attributeValue)
    head.appendChild(newElement)
    return newElement
}

private fun Document.ensureLinkTag(rel: String): HTMLLinkElement {
    val head: Element = head ?: error("Document head is not available")
    val selector: String = "link[rel='$rel']"
    val existingElement: HTMLLinkElement? = head.querySelector(selector) as? HTMLLinkElement
    if (existingElement != null) {
        return existingElement
    }

    val newElement: HTMLLinkElement = createElement("link") as HTMLLinkElement
    newElement.setAttribute("rel", rel)
    head.appendChild(newElement)
    return newElement
}

fun Document.setPageMeta(titleText: String, description: String) {
    title = titleText

    val descriptionMeta: HTMLMetaElement = ensureMetaTag(attributeName = "name", attributeValue = "description")
    descriptionMeta.setAttribute("content", description)
}

fun Document.setCanonicalUrl(url: String) {
    val canonicalLink: HTMLLinkElement = ensureLinkTag(rel = "canonical")
    canonicalLink.setAttribute("href", url)
}

fun Document.setOpenGraphTag(property: String, content: String) {
    val metaElement: HTMLMetaElement = ensureMetaTag(attributeName = "property", attributeValue = property)
    metaElement.setAttribute("content", content)
}

fun Document.setMetaNameTag(name: String, content: String) {
    val metaElement: HTMLMetaElement = ensureMetaTag(attributeName = "name", attributeValue = name)
    metaElement.setAttribute("content", content)
}

@Composable
fun Seo(title: String, description: String) {
    LaunchedEffect(title, description) {
        document.setPageMeta(titleText = title, description = description)
    }
}

@Composable
fun HtmlLangRo() {
    LaunchedEffect(Unit) {
        document.documentElement?.setAttribute("lang", "ro")
    }
}

@Composable
fun ApplicationLdJson(json: String) {
    LaunchedEffect(json) {
        val head: Element = document.head ?: return@LaunchedEffect
        val scriptId = "rezident-info-ld-json"
        val existingScript: HTMLScriptElement? = document.getElementById(scriptId) as? HTMLScriptElement
        val scriptElement: HTMLScriptElement =
            existingScript ?: (document.createElement("script") as HTMLScriptElement).also {
                it.id = scriptId
                it.type = "application/ld+json"
                head.appendChild(it)
            }
        scriptElement.textContent = json
    }
}
